// ═══════════════════════════════════════════════════════════════════════════
// Cómo y cuándo se paga — la regla que el SAT no negocia
//
// El operador: "Si una remisión es PPD, el método de pago es Por definir; no
// puede ser otro."
//
// Y es literal, no una convención de la casa. Una factura PPD dice "esto se va
// a pagar en parcialidades o después"; declarar de una vez que se pagó por
// transferencia es contradecirse en el mismo documento. Por eso el SAT exige
// la clave 99 —"por definir"— y por eso existe el complemento de pago: para
// decir DESPUÉS, cuando ya se sabe, cómo entró cada parcialidad.
//
// LO QUE ESTO CORRIGE. La app dejaba elegir cualquier combinación, así que se
// podía emitir una PPD con forma 03 y descubrirlo cuando el SAT la rechazara —
// o peor, cuando la aceptara mal. Ahora la combinación imposible no se puede
// ni construir.
//
// Va aquí, sin React y sin base, porque la misma regla tiene que valer en los
// tres sitios donde se decide: al crear la remisión, al cambiarla antes de
// timbrar, y en Postgres.
// ═══════════════════════════════════════════════════════════════════════════

/** La clave del SAT para "todavía no se sabe cómo se va a pagar". */
export const POR_DEFINIR = "99";

/** Pago en parcialidades o diferido. */
export const PPD = "PPD";

/**
 * Ajusta la forma de pago a lo que el método permite.
 *
 * PPD obliga a 99. Al revés no: una PUE puede pagarse de cualquier forma, y si
 * venía en 99 se deja en 99 —no se inventa una— porque "no sé cómo me van a
 * pagar de una sola vez" es una respuesta legítima que el usuario tendrá que
 * completar antes de timbrar.
 */
export function ajustarFormaPago(metodo, forma) {
  if (metodo === PPD) return POR_DEFINIR;
  return forma || "";
}

/** ¿Esta pareja se puede timbrar? */
export function validarPago({ metodo, forma }) {
  if (metodo === PPD && forma !== POR_DEFINIR) {
    return { ok: false,
      motivo: `Una factura ${PPD} debe llevar forma de pago ${POR_DEFINIR} (por definir): ` +
              `todavía no se sabe cómo se va a pagar. Se dice después, en el ` +
              `complemento de cada pago.` };
  }
  if (metodo !== PPD && forma === POR_DEFINIR) {
    return { ok: false,
      motivo: `Una factura ${metodo || "PUE"} se paga de una sola vez, así que hay que ` +
              `decir cómo. La clave ${POR_DEFINIR} solo vale en las PPD.` };
  }
  if (!forma) return { ok: false, motivo: "Falta la forma de pago." };
  if (!metodo) return { ok: false, motivo: "Falta el método de pago." };
  return { ok: true };
}

/**
 * ¿Se puede todavía cambiar cómo se paga?
 *
 * Mientras no haya CFDI vigente, sí: la remisión es un documento interno y
 * completar cómo se va a cobrar no falsea nada. Con CFDI vigente, no — eso ya
 * se le declaró al SAT y cambiarlo aquí desalinearía los dos, que es la misma
 * regla que impide editar una timbrada.
 */
export function puedeCambiarPago(inv) {
  if (!inv) return { ok: false, motivo: "No hay remisión." };
  if (inv.canceledAt) return { ok: false, motivo: "La remisión está cancelada." };
  const timbrada = (inv.cfdiId || inv.cfdiUuid) && inv.cfdiStatus !== "canceled";
  if (timbrada) {
    return { ok: false,
      motivo: "Ya tiene CFDI vigente: cómo se paga ya se le declaró al SAT. " +
              "Para cambiarlo hay que cancelar el comprobante primero." };
  }
  return { ok: true };
}
